import type { AircraftCatalog, ArrivalInquiry, CatalogEntry, ZoneGeometry, ZoneSlot } from './types'
import { alongFootprints, buildSlotGrid, computeUniformExtent } from './buildSlotGrid'
import { scheduleReuse, peakConcurrent } from './scheduleReuse'

/**
 * Pure self-checks for the time-aware placement engine. No test runner exists in
 * this project, so these run in dev (logged once by the bootstrap hook) to guard
 * the two footguns: the Three.js Y-rotation in the slot grid, and the interval
 * reuse / prefix-stability of the scheduler. Eyeballing 3 planes in-browser via
 * the PlacementDebugOverlay complements these.
 */
export interface CheckResult {
  name: string
  pass: boolean
  detail?: string
}

function approx(a: number, b: number, eps = 0.01): boolean {
  return Math.abs(a - b) <= eps
}

/** Minimal catalog — the engine only reads `.footprint`. */
function makeCatalog(entries: Array<{ code: string; wingspan: number; length: number }>): AircraftCatalog {
  const m: AircraftCatalog = new Map()
  for (const e of entries) {
    m.set(e.code, { footprint: { wingspan: e.wingspan, length: e.length } } as unknown as CatalogEntry)
  }
  return m
}

function inq(scheduleId: string, code: string, arrival: number, departure: number): ArrivalInquiry {
  return {
    scheduleId,
    aircraftId: scheduleId,
    tailNumber: scheduleId,
    typeCode: code,
    operatorName: '',
    arrival,
    departure,
    estimatedArrival: arrival,
    estimatedDeparture: departure,
    arrivalStatus: 'on_time',
    confidence: 'confirmed',
    triage: 'green',
    services: [],
    isVip: false,
    contacts: [],
  }
}

function zone(overrides: Partial<ZoneGeometry> = {}): ZoneGeometry {
  return {
    areaId: 'Z',
    name: 'Z',
    center: [0, 0],
    rotationDeg: 0,
    width: 300,
    depth: 150,
    pattern: 'row',
    progression: 'side_by_side',
    coupling: null,
    lanes: 1,
    safetyMarginFt: 15,
    placementHeadingDeg: 90,
    pitchMode: 'uniform',
    fillPriority: 10,
    ...overrides,
  }
}

const CATALOG = makeCatalog([{ code: 'J', wingspan: 50, length: 50 }])

export function runEngineSelfChecks(): CheckResult[] {
  const results: CheckResult[] = []
  const add = (name: string, pass: boolean, detail?: string) => results.push({ name, pass, detail })

  // 1. Grid geometry, rotation 0: 300-wide, margin 15, extent 60 → 4 slots at
  //    X = -105, -30, 45, 120, all Z = 0, heading 90.
  {
    const slots = buildSlotGrid(zone(), 60)
    const xs = slots.map((s) => s.position[0])
    const ok =
      slots.length === 4 &&
      approx(xs[0], -105) &&
      approx(xs[3], 120) &&
      slots.every((s) => approx(s.position[1], 0)) &&
      slots.every((s) => s.rotationDeg === 90)
    add('grid-rot0', ok, ok ? undefined : `len=${slots.length} xs=${xs.join(',')}`)
  }

  // 2. Rotation 90 maps the lane (local X) onto scene Z. First slot was
  //    [-105, 0] at rot 0 → [0, 105] at rot 90 (locks the Y-rotation).
  {
    const slots = buildSlotGrid(zone({ rotationDeg: 90 }), 60)
    const ok =
      slots.length === 4 && approx(slots[0].position[0], 0) && approx(slots[0].position[1], 105)
    add('grid-rot90', ok, ok ? undefined : `p0=${slots[0]?.position.join(',')}`)
  }

  // Helper: a grid with exactly N slots from a known width.
  const grid1 = (() => {
    const g = buildSlotGrid(zone({ width: 90, depth: 80 }), 60) // one slot
    return [{ areaId: 'Z', name: 'Z', fillPriority: 10, uniformExtentFt: 60, progression: 'side_by_side' as const, slots: g }]
  })()
  const grid2 = (() => {
    const g = buildSlotGrid(zone({ width: 165, depth: 80 }), 60) // two slots
    return [{ areaId: 'Z', name: 'Z', fillPriority: 10, uniformExtentFt: 60, progression: 'side_by_side' as const, slots: g }]
  })()

  add('grid1-has-1-slot', grid1[0].slots.length === 1, `len=${grid1[0].slots.length}`)
  add('grid2-has-2-slots', grid2[0].slots.length === 2, `len=${grid2[0].slots.length}`)

  // 3. Non-overlapping stays SHARE one slot (the reuse win).
  {
    const r = scheduleReuse([inq('A', 'J', 0, 10), inq('B', 'J', 10, 20)], grid1, CATALOG)
    const ok =
      r.slots.length === 2 &&
      r.unplaced.length === 0 &&
      approx(r.slots[0].position[0], r.slots[1].position[0]) &&
      approx(r.slots[0].position[1], r.slots[1].position[1])
    add('reuse-shares-slot', ok, ok ? undefined : `placed=${r.slots.length} unplaced=${r.unplaced.length}`)
  }

  // 4a. Overlapping stays cannot share one slot → second overflows.
  {
    const r = scheduleReuse([inq('A', 'J', 0, 10), inq('B', 'J', 5, 15)], grid1, CATALOG)
    const ok = r.slots.length === 1 && r.unplaced.length === 1 && r.unplaced[0] === 'B'
    add('conflict-1slot-overflows', ok, ok ? undefined : `placed=${r.slots.length} unplaced=${r.unplaced.join(',')}`)
  }

  // 4b. With two slots, overlapping stays both fit (different slots).
  {
    const r = scheduleReuse([inq('A', 'J', 0, 10), inq('B', 'J', 5, 15)], grid2, CATALOG)
    const distinct = r.slots.length === 2 && !approx(r.slots[0].position[0], r.slots[1].position[0])
    add('conflict-2slots-both-fit', distinct && r.unplaced.length === 0)
  }

  // 5. Peak concurrency = busiest instant occupancy.
  {
    const peak = peakConcurrent([
      [0, 10],
      [5, 15],
      [20, 30],
    ])
    add('peak-concurrent', peak === 2, `peak=${peak}`)
  }

  // 6. Prefix stability: schedule(K) and schedule(K+1) agree on the first K.
  {
    const fleet = [
      inq('A', 'J', 0, 10),
      inq('B', 'J', 10, 20),
      inq('C', 'J', 0, 5),
      inq('D', 'J', 6, 9),
    ]
    const k3 = scheduleReuse(fleet.slice(0, 3), grid2, CATALOG)
    const k4 = scheduleReuse(fleet.slice(0, 4), grid2, CATALOG)
    const byId = (slots: typeof k3.slots) => new Map(slots.map((s) => [s.scheduleId, s]))
    const m3 = byId(k3.slots)
    const m4 = byId(k4.slots)
    let stable = m3.size === 3
    for (const [id, s] of m3) {
      const t = m4.get(id)
      if (!t || !approx(t.position[0], s.position[0]) || !approx(t.position[1], s.position[1])) {
        stable = false
      }
    }
    add('prefix-stable', stable)
  }

  // 7. Formation density ordering: free_stack ≥ checkerboard ≥ double_row ≥ row.
  //    A 300×300 zone, extent 60 both axes → row=4, double_row=8, then the 2D
  //    patterns stack rows along depth (checkerboard staggers, free_stack fills).
  {
    const n = (pattern: ZoneGeometry['pattern'], lanes = 1, coupling: ZoneGeometry['coupling'] = null) =>
      buildSlotGrid(zone({ pattern, lanes, coupling, width: 300, depth: 300 }), 60, 60).length
    const row = n('row', 1)
    const dbl = n('double_row', 2)
    const chk = n('checkerboard', 1)
    const free = n('free_stack', 1)
    const ok = free >= chk && chk >= dbl && dbl >= row && row > 0
    add('formation-density-order', ok, `row=${row} dbl=${dbl} chk=${chk} free=${free}`)
  }

  // 8. free_stack produces no two slots at the same position (a real grid).
  {
    const slots = buildSlotGrid(zone({ pattern: 'free_stack', width: 300, depth: 300 }), 60, 60)
    const keys = new Set(slots.map((s) => `${s.position[0].toFixed(1)},${s.position[1].toFixed(1)}`))
    add('free-stack-no-dup-positions', keys.size === slots.length && slots.length > 4, `slots=${slots.length} unique=${keys.size}`)
  }

  // 9. tail_to_tail flips alternate rows → both base (90) and 270 headings present.
  {
    const slots = buildSlotGrid(
      zone({ pattern: 'double_row', lanes: 2, coupling: 'tail_to_tail', depth: 300 }),
      60,
      60
    )
    const headings = new Set(slots.map((s) => s.rotationDeg))
    add('tail-to-tail-two-headings', headings.has(90) && headings.has(270), `h=${[...headings].join(',')}`)
  }

  // 10. nested packs more rows than same_direction in the same depth (tighter stride).
  {
    const nested = buildSlotGrid(
      zone({ pattern: 'free_stack', coupling: 'nested', tailWidthRatio: 0.5, width: 300, depth: 300 }),
      60,
      60
    ).length
    const plain = buildSlotGrid(
      zone({ pattern: 'free_stack', coupling: 'same_direction', width: 300, depth: 300 }),
      60,
      60
    ).length
    add('nested-packs-more', nested >= plain, `nested=${nested} plain=${plain}`)
  }

  // 11. Variable pitch sizes slots smaller than uniform for a spread fleet.
  {
    const spread = makeCatalog([
      { code: 'S', wingspan: 40, length: 40 },
      { code: 'M', wingspan: 50, length: 50 },
      { code: 'L', wingspan: 60, length: 60 },
      { code: 'XL', wingspan: 90, length: 90 },
    ])
    const fleet = [inq('a', 'S', 0, 1), inq('b', 'M', 0, 1), inq('c', 'L', 0, 1), inq('d', 'XL', 0, 1)]
    const uni = computeUniformExtent(zone({ pitchMode: 'uniform' }), fleet, spread)
    const vary = computeUniformExtent(zone({ pitchMode: 'variable' }), fleet, spread)
    add('variable-pitch-denser', vary < uni, `uniform=${uni} variable=${vary}`)
  }

  // 12. checkerboard + tail_to_tail = nose-to-nose interleave: alternate rows
  //     flip heading 180° AND shift half a slot (extent/2 = 30) in the lane.
  {
    const slots = buildSlotGrid(
      zone({ pattern: 'checkerboard', coupling: 'tail_to_tail', width: 300, depth: 300 }),
      60,
      60
    )
    const headings = new Set(slots.map((s) => s.rotationDeg))
    const row0 = slots.filter((s) => s.laneIndex === 0).sort((a, b) => a.position[0] - b.position[0])
    const row1 = slots.filter((s) => s.laneIndex === 1).sort((a, b) => a.position[0] - b.position[0])
    const staggered =
      row0.length > 0 && row1.length > 0 && approx(Math.abs(row1[0].position[0] - row0[0].position[0]), 30)
    const ok = headings.has(90) && headings.has(270) && staggered
    add('checkerboard-interleave-opposing', ok, `h=${[...headings].join(',')} stag=${staggered}`)
  }

  // 13. nested = zipper: opposing headings + half-slot stagger + tighter stride.
  {
    const slots = buildSlotGrid(
      zone({ pattern: 'free_stack', coupling: 'nested', tailWidthRatio: 0.5, width: 300, depth: 300 }),
      60,
      60
    )
    const headings = new Set(slots.map((s) => s.rotationDeg))
    const row0 = slots.filter((s) => s.laneIndex === 0).sort((a, b) => a.position[0] - b.position[0])
    const row1 = slots.filter((s) => s.laneIndex === 1).sort((a, b) => a.position[0] - b.position[0])
    const staggered =
      row0.length > 0 && row1.length > 0 && approx(Math.abs(row1[0].position[0] - row0[0].position[0]), 30)
    const ok = headings.has(90) && headings.has(270) && staggered
    add('nested-zipper-opposing-staggered', ok, `h=${[...headings].join(',')} stag=${staggered}`)
  }

  // 14. footprintRows packs rows from the front edge (tighter) vs the simplified
  //     even-spread spacing — the lead row sits nearer the front.
  {
    const z = (footprintRows: boolean) =>
      buildSlotGrid(zone({ pattern: 'double_row', lanes: 2, depth: 300, footprintRows }), 60, 60)
    const front = (slots: ZoneSlot[]) =>
      Math.min(...[...new Set(slots.map((s) => Math.round(s.position[1])))])
    const simp = front(z(false))
    const tight = front(z(true))
    add('footprint-rows-pack-front', tight < simp, `tight=${tight} simp=${simp}`)
  }

  // 15. Nose-to-Nose (checkerboard) mirrors the flip parity of Tail-to-Tail
  //     (double_row): row 0 faces the opposite way, so the two are distinct.
  {
    const row0Heading = (pattern: ZoneGeometry['pattern']) => {
      const slots = buildSlotGrid(
        zone({ pattern, lanes: 2, coupling: 'tail_to_tail', depth: 300 }),
        60,
        60
      )
      return slots.find((s) => s.laneIndex === 0)?.rotationDeg
    }
    const tail = row0Heading('double_row') // tails to spine: row0 = base (90)
    const nose = row0Heading('checkerboard') // noses to spine: row0 = base+180 (270)
    add('nose-vs-tail-row0-differs', tail === 90 && nose === 270, `tail=${tail} nose=${nose}`)
  }

  // 16. Row-count floor: a shallow area that fits only one packed row still
  //     renders `lanes` opposing rows (so Nose-to-Nose / Nested stay visible).
  {
    const slots = buildSlotGrid(
      zone({ pattern: 'checkerboard', lanes: 2, coupling: 'tail_to_tail', depth: 80 }),
      60,
      60
    )
    const rows = new Set(slots.map((s) => s.laneIndex))
    const headings = new Set(slots.map((s) => s.rotationDeg))
    add('shallow-keeps-two-rows', rows.size === 2 && headings.size === 2, `rows=${rows.size} h=${headings.size}`)
  }

  // 17. Wing-to-wing (variable) lays per-aircraft slot sizes and packs more than
  //     one uniform box for a spread fleet.
  {
    const sizes = [40, 50, 60, 90]
    const zVar = zone({ pitchMode: 'variable', pattern: 'row', lanes: 1, width: 600, depth: 80 })
    const zUni = zone({ pitchMode: 'uniform', pattern: 'row', lanes: 1, width: 600, depth: 80 })
    const varSlots = buildSlotGrid(zVar, 90, 90, sizes)
    const uniSlots = buildSlotGrid(zUni, 90, 90)
    const extents = new Set(varSlots.map((s) => s.extentFt))
    const ok = extents.size > 1 && varSlots.length > uniSlots.length
    add('wing-to-wing-variable-slots', ok, `extents=${[...extents].join(',')} var=${varSlots.length} uni=${uniSlots.length}`)
  }

  // 18. Wing-to-wing slot sizes are weighted by fleet COMPOSITION (a multiset), not
  //     the set of distinct hulls — so a fleet dominated by light types lays down
  //     mostly small slots (lights pack tight) instead of alternating small/large and
  //     scattering the lights. The old unique-set laydown returned just [40, 90].
  {
    const cat = makeCatalog([
      { code: 'S', wingspan: 40, length: 40 },
      { code: 'L', wingspan: 90, length: 90 },
    ])
    const fleet = [
      inq('s1', 'S', 0, 10),
      inq('s2', 'S', 0, 10),
      inq('s3', 'S', 0, 10),
      inq('s4', 'S', 0, 10),
      inq('l1', 'L', 0, 10),
    ]
    const seq = alongFootprints(zone({ pitchMode: 'variable' }), fleet, cat)
    const smalls = seq.filter((e) => e === 40).length
    const bigs = seq.filter((e) => e === 90).length
    add(
      'wing-to-wing-composition-weighted',
      seq.length === 5 && smalls === 4 && bigs === 1,
      `seq=${seq.join(',')}`
    )
  }

  return results
}
